'use strict';

import React, { Component, PropTypes } from 'react';
import moment from 'moment';
import gql from 'graphql-tag';
import { CopyIcon, Switch, Tooltip, Button, Icon } from './';

// Import the CSS
import '../../scss/components/_subdivision.scss';

export default class Subdivision extends Component {
	constructor(props) {
		super(props);
		this.onPaidToggle = this.onPaidToggle.bind(this);
		this.onZlsEnabledToggle = this.onZlsEnabledToggle.bind(this);
		this.onRowClick = this.onRowClick.bind(this);
		this.onVerifyClick = this.onVerifyClick.bind(this);
		this.onReprovisionClick = this.onReprovisionClick.bind(this);
		this.onReprocessImagesClick = this.onReprocessImagesClick.bind(this);
		this.onInPersonTourToggle = this.onInPersonTourToggle.bind(this);
		this.onVirtualTourToggle = this.onVirtualTourToggle.bind(this);
		this.onPearlEligibilityToggle = this.onPearlEligibilityToggle.bind(this);
		this.renderTooltipText = this.renderTooltipText.bind(this);

		this.state = {
			isPCToggleLoading: false,
		};
	}

	generateLoopTemplateName = subdiv => {
		return `(${subdiv.rawUniqueId}) ${subdiv.name} - ${subdiv.corporation.name}`;
	};

	async onPaidToggle() {
		this.setState({ isPCToggleLoading: true });

		const shouldRevertPending =
			this.props.subdivision.isFeedEnabled && this.props.subdivision.isPendingFeedDisablement;
		const newFeedEnabledValue = shouldRevertPending || !this.props.subdivision.isFeedEnabled;
		try {
			// toggling syndication will always want to set isPendingFeedDisablement to false
			await this.props.onPCToggle(this.props.subdivision, newFeedEnabledValue, false);
		} catch (err) {
			this.props.onError(err.message);
		} finally {
			this.setState({ isPCToggleLoading: false });
		}
	}

	onZlsEnabledToggle() {
		if (
			!this.props.subdivision.isFeedEnabled ||
			this.props.subdivision.isPendingFeedDisablement ||
			this.props.subdivision.deletedAt
		) {
			return;
		}
		const newValue = !this.props.subdivision.isZlsEnabled;

		this.props.onZlsToggle(this.props.subdivision, newValue);
	}

	onRowClick(e) {
		if (e.isDefaultPrevented() || this.props.subdivision.deletedAt) {
			return;
		}

		e.preventDefault();

		this.props.onClick(this.props.subdivision);
	}

	onVerifyClick(e) {
		e.stopPropagation();
		this.props.onClickVerify(this.props.subdivision);
	}

	onReprovisionClick(e) {
		this.props.onReprovisionClick(e, this.props.subdivision);
	}

	onReprocessImagesClick(e) {
		this.props.onReprocessImagesClick(e, this.props.subdivision);
	}

	onInPersonTourToggle() {
		this.props.onInPersonTourToggle(
			this.props.subdivision,
			!this.props.subdivision.isTourEnabled,
		);
	}

	onVirtualTourToggle() {
		this.props.onVirtualTourToggle(
			this.props.subdivision,
			!this.props.subdivision.isVirtualTourEnabled,
		);
	}

	onPearlEligibilityToggle() {
		this.props.onPearlEligibilityToggle(
			this.props.subdivision,
			!this.props.subdivision.isPearlEligible,
		);
	}

	renderTooltipText({ title, text }) {
		return (
			<div>
				{title && <div className="tooltip-title">{title}</div>}
				<div className="tooltip-text">{text}</div>
			</div>
		);
	}

	render() {
		const subdiv = this.props.subdivision;
		const isPCChecked = subdiv.isFeedEnabled && !subdiv.isPendingFeedDisablement;
		const hasZlsActiveLots =
			isPCChecked && !subdiv.isZlsEnabled && !!subdiv.lots.counts.zlsActive;

		const classes = ['row', 'table-row', 'subdivision'];

		if (subdiv.deletedAt) {
			classes.push('deleted');
		}

		if (!subdiv.isVerified) {
			classes.push('notVerified');
		}

		if (subdiv.isFeedEnabled) {
			classes.push('paid');
		} else {
			classes.push('unpaid');
		}

		const zlsContainerClasses = ['zls-container'];
		if (!subdiv.dlTemplateId) {
			zlsContainerClasses.push('needs-template');
		}

		const zlsSwitchClasses = ['toggle', 'zls'];
		if (!isPCChecked) {
			zlsSwitchClasses.push('disabled');
		}

		const loopTemplateName = this.generateLoopTemplateName(subdiv);

		return (
			<div className={classes.join(' ')} onClick={this.onRowClick}>
				<div className="table-cell subdivision-info-column">
					<div>
						<strong>{subdiv.name}</strong>
					</div>
					<div>
						<span className="titles">Number</span>
						{subdiv.number}
					</div>
					<div>
						<span className="titles">Provider Listing ID</span>
						{subdiv.rawUniqueId}
						<div className="copy-icon-container">
							<CopyIcon
								copyText={subdiv.rawUniqueId}
								text="Copy Provider Listing ID"
							/>
						</div>
					</div>
					<If condition={this.props.user.isAdmin()}>
						<div>
							<span className="titles">Pearl Management</span>
							<Switch
								onLabel="On"
								offLabel="Off"
								className="toggle pearl"
								name={'pearl-eligibility-switch-' + subdiv.rawId}
								checked={subdiv.isPearlEligible}
								onChange={this.onPearlEligibilityToggle}
							/>
						</div>
					</If>
					<If condition={this.props.user.isAdmin() && subdiv.isVerified}>
						<div className="reprovision">
							<Button onClick={this.onReprovisionClick}>
								<Icon type="plus" />
								Reprovision Tracked Phone Number
							</Button>
						</div>
						<div className="reprocess-images">
							<Button onClick={this.onReprocessImagesClick}>
								<Icon type="report" />
								Reprocess Community Images
							</Button>
						</div>
					</If>
					<If condition={subdiv.statusLabel === 'ComingSoon'}>
						<div>
							<span className="coming-soon">
								Available Soon -{' '}
								{subdiv.isComingSoonInferred ? 'Inferred' : 'Builder Set'}
							</span>
						</div>
					</If>
					<If condition={subdiv.deletedAt}>
						<div>
							<span className="titles">Deleted</span>
							{moment(subdiv.deletedAt).format('MM/DD/YYYY')}
						</div>
					</If>
				</div>

				<div className="table-cell subdivision-sales-office-column">
					<div className="row cell-label">
						<div className="cell-title">Sales Office</div>
					</div>
					<div className="row">
						<Choose>
							<When condition={subdiv.salesStreetOne}>
								{subdiv.salesStreetOne}
								<br />
								<If condition={subdiv.salesStreetTwo}>
									{subdiv.salesStreetTwo}
									<br />
								</If>
								{subdiv.salesCity}, {subdiv.salesState}
								<br />
								{subdiv.salesZip}
							</When>
							<Otherwise>None</Otherwise>
						</Choose>
					</div>
				</div>

				<div className="table-cell subdivision-lots-column">
					<div className="row cell-label">
						<div className="cell-title">Total Lots</div>
					</div>
					<div className="row justify-center">{subdiv.lotCount}</div>
				</div>

				<div className="table-cell subdivision-syndication-column">
					<div className="row cell-label">
						<div className="cell-title">Syndication</div>
					</div>
					<div className="switch-label cell-label">PC Classic</div>
					<div className="row justify-center">
						{!this.state.isPCToggleLoading && subdiv.isPendingFeedDisablement && (
							<Tooltip
								position="left"
								style="pc-pending"
								text={this.renderTooltipText({
									title: 'Active or Pending ZLS listings',
									text:
										'The community will be updated to Unpaid automatically once there are no more active ZLS listings',
								})}>
								<img src="/assets/img/timer.svg" />
							</Tooltip>
						)}
						<Choose>
							<When condition={this.props.user.can('set-paid-status')}>
								<Switch
									onLabel="On"
									offLabel={subdiv.isPendingFeedDisablement ? 'Pending' : 'Off'}
									className="toggle"
									name={'pc-switch-' + subdiv.rawId}
									checked={isPCChecked}
									onChange={this.onPaidToggle}
									isLoading={this.state.isPCToggleLoading}
								/>
							</When>
							<When condition={!this.props.user.can('set-paid-status')}>
								{subdiv.isFeedEnabled ? 'Paid' : 'Unpaid'}
							</When>
						</Choose>
					</div>

					{subdiv.isZlsEligible && (
						<div className={zlsContainerClasses.join(' ')}>
							<div className="zls-container-enablement">
								<div className="zls-container-switch">
									<span className="zls-container-switch-text">ZLS Publish</span>
									{hasZlsActiveLots && (
										<Tooltip
											position="left"
											style="zls-warn"
											text={this.renderTooltipText({
												title: 'Active or Pending ZLS listings',
												text:
													'ZLS billing will continue until these listings are resolved',
											})}>
											<img src="/assets/img/warning.svg" />
										</Tooltip>
									)}
									<Choose>
										<When condition={this.props.user.can('set-paid-status')}>
											<Switch
												onLabel="On"
												offLabel={isPCChecked ? 'Off' : ''}
												className={zlsSwitchClasses.join(' ')}
												style={isPCChecked ? '' : 'disabled'}
												name={'zls-switch-' + subdiv.rawId}
												checked={subdiv.isZlsEnabled}
												onChange={this.onZlsEnabledToggle}
											/>
										</When>
									</Choose>
								</div>
							</div>

							<div className="zls-container-configured">
								<img
									src={`/assets/img/${
										subdiv.dlTemplateId ? 'checkmark' : 'warning'
									}.svg`}
								/>
								<span>{`${subdiv.dlTemplateId ? '' : 'Not '}Configured`}</span>
								<CopyIcon
									copyText={loopTemplateName}
									text="Copy loop template name"
								/>
							</div>
							<span>{loopTemplateName}</span>
						</div>
					)}
				</div>

				<div className="table-cell subdivision-tour-switch-column">
					<div className="row cell-label">
						<div className="cell-title">Tour Booking</div>
					</div>
					<div className="switch-label cell-label">In-Person</div>
					<div className="row justify-center">
						<Switch
							onLabel="On"
							offLabel="Off"
							className="toggle tour"
							name={'tour-switch-' + subdiv.rawId}
							checked={subdiv.isTourEnabled}
							onChange={this.onInPersonTourToggle}
						/>
					</div>

					<div className="switch-label cell-label">Virtual</div>
					<div className="row justify-center">
						<Switch
							onLabel="On"
							offLabel="Off"
							className="toggle tour"
							name={'virtual-tour-switch-' + subdiv.rawId}
							checked={subdiv.isVirtualTourEnabled}
							onChange={this.onVirtualTourToggle}
						/>
					</div>
				</div>

				<div className="table-cell subdivision-status-column">
					<div className="row cell-label">
						<div className="cell-title">Status</div>
					</div>
					<div className="row verify-column" onClick={this.onVerifyClick}>
						<div className="verify-link">
							<If condition={!subdiv.isVerified}>
								<img src="/assets/img/warning.svg" />
							</If>
							<a className="verify-button">
								{subdiv.isVerified ? 'Verified' : 'Verify'}
							</a>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

Subdivision.fragments = {
	subdivision: gql`
		fragment SubdivisionResults on SubdivisionCollection {
			results {
				id
				rawId: id(raw: true)
				uniqueId
				rawUniqueId: uniqueId(raw: true)
				customerId
				name
				number
				statusLabel
				description
				website
				isActive
				isComingSoonInferred
				isFeedEnabled
				isPendingFeedDisablement
				subImage
				salesStreetOne
				salesStreetTwo
				salesCity
				salesState
				salesZip
				salesLatitude
				salesLongitude
				salesPhoneAreaCode
				salesPhonePrefix
				salesPhoneSuffix
				subStreetOne
				subStreetTwo
				subCity
				subState
				subZip
				subLatitude
				subLongitude
				sourceFeed {
					id
					rawId: id(raw: true)
					name
					source
				}
				lotCount
				isVerified
				deletedAt
				isPearlEligible
				isTourEnabled
				isVirtualTourEnabled
				isZlsEnabled
				isZlsEligible
				dlTemplateId
				corporation {
					id
					name
				}
				lots {
					counts {
						zlsActive
					}
				}
			}
			numTotal
		}
	`,
};

Subdivision.propTypes = {
	user: PropTypes.object.isRequired,
	subdivision: PropTypes.object.isRequired,
	onPCToggle: PropTypes.func.isRequired,
	onZlsToggle: PropTypes.func.isRequired,
	onInPersonTourToggle: PropTypes.func.isRequired,
	onVirtualTourToggle: PropTypes.func.isRequired,
	onPearlEligibilityToggle: PropTypes.func.isRequired,
	onClick: PropTypes.func.isRequired,
	onClickVerify: PropTypes.func.isRequired,
	onReprovisionClick: PropTypes.func.isRequired,
	onReprocessImagesClick: PropTypes.func.isRequired,
	onError: PropTypes.func.isRequired,
};



// WEBPACK FOOTER //
// ./src/assets/js/components/Subdivision.js